/**
 * Explore-map geometry: where the map should open, which pins are close enough
 * on screen that they have to be merged into one bubble, and which bubble sits
 * on top when they still overlap.
 *
 * Lives outside the screen (and imports no react-native) for two reasons:
 * the three render paths — iOS `<Marker>`, the Android absolute overlay, and
 * the web Google-Maps overlays — must agree pin-for-pin, and the maths is
 * worth testing without a renderer. The last time pin logic lived inline in
 * the screen the three paths drifted apart (see the `relation` comment in
 * `getAvailableVisits`).
 *
 * Background — why any of this exists. Until 2026-08-25 the map opened at a
 * region that fitted *every* pin in the feed with no upper bound, and drew
 * every pin as its own 88pt bubble with nothing deciding which one sits on
 * top. A nurse in Sheepshead Bay with one broadcast visit 90 miles upstate
 * therefore opened the map ~150 miles tall, where an 88pt bubble covers about
 * 16 miles of ground and all 31 of her local pins landed inside one bubble
 * width of each other. Her words: "if a new bubble pops up and I try to tap
 * it to pick up the visit, I will end up tapping one of these instead."
 */

export interface MapRegion {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
}

/** The bit of a visit this module needs. Keeps the maths testable without
 *  building a whole `AvailableVisitListItem`. */
export interface ClusterablePin {
    id: number;
    lat: number;
    lng: number;
    /** Server-computed miles from the RN's primary address. `0` means the
     *  server could not compute one (she has no geocoded address), NOT that
     *  the visit is on her doorstep — see `hasUsableDistances`. */
    distanceMiles: number;
}

/** Downtown Brooklyn. Only used when the feed is empty, so the map opens on
 *  something recognisable rather than the middle of the Atlantic. */
const FALLBACK_CENTER = { latitude: 40.6892, longitude: -73.9857 };

/** Never open tighter than this (~4 miles tall). A single pin would otherwise
 *  produce a zero-size region. */
export const MIN_REGION_DELTA = 0.06;

/**
 * Never open wider than this (~21 miles tall). This is the cap that was
 * missing: wide enough to hold a real five-borough working area, tight enough
 * that streets are still legible. Anything further out is reachable by
 * pinching, and pinching is something she chooses rather than something she is
 * forced into on every single open.
 */
export const MAX_REGION_DELTA = 0.3;

/**
 * Pins further than this from her home are left out of the *opening* region.
 * They are still drawn — she can pan to them, and they still appear in the
 * cards list — they just don't get a vote on how far out the map starts.
 *
 * Tuned against a real Brooklyn feed: at 12 miles most of her own assigned
 * visits fell outside the vote and the map opened tight on a handful of pins
 * with the rest off-screen, which trades one framing problem for another.
 */
export const OPENING_RADIUS_MILES = 15;

/** Margin around the pins the opening region has to hold. The old code used
 *  1.8, which with a cap in place mostly just pinned every region to the cap. */
const OPENING_PADDING = 1.25;

/** A patient with no geocoded address arrives as `lat: 0, lng: 0` (the `?? 0`
 *  in `getAvailableVisits`). That is a real place — the Gulf of Guinea — so a
 *  single one of them would drag the opening region across the Atlantic and
 *  park a bubble there. Drop them from the map entirely; the cards list still
 *  shows the visit, which is the only surface that can honestly represent a
 *  visit whose location we don't know. */
export function hasMappableLocation(pin: { lat: number; lng: number }): boolean {
    return Number.isFinite(pin.lat) && Number.isFinite(pin.lng) && (pin.lat !== 0 || pin.lng !== 0);
}

/** True when the server was able to compute real distances for this feed. All
 *  zeroes means the RN has no geocoded primary address, so distance can't be
 *  used to decide what is "near her". */
function hasUsableDistances(pins: ClusterablePin[]): boolean {
    return pins.some((pin) => pin.distanceMiles > 0);
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Which pins get a say in the opening region.
 *
 * Normally: everything within `OPENING_RADIUS_MILES`. If she has nothing that
 * close (a nurse in a thin coverage area — Watertown has one bookable RN and
 * Binghamton none), fall back to a band around her *nearest* pin rather than
 * to nothing, so the map still opens somewhere useful instead of on the
 * default Brooklyn centre.
 */
function pinsForOpeningRegion(pins: ClusterablePin[]): ClusterablePin[] {
    if (!hasUsableDistances(pins)) {
        return pins;
    }

    const near = pins.filter((pin) => pin.distanceMiles <= OPENING_RADIUS_MILES);
    if (near.length > 0) {
        return near;
    }

    const nearest = Math.min(...pins.map((pin) => pin.distanceMiles));
    return pins.filter((pin) => pin.distanceMiles <= nearest + OPENING_RADIUS_MILES);
}

/**
 * Where the map should open.
 *
 * Centre is the median pin, not the bounding-box centre: with the region
 * clamped, a bbox centre pulled toward one far pin can leave the dense cluster
 * she actually works in off-screen, whereas the median always sits inside it.
 */
export function openingRegion(pins: ClusterablePin[]): MapRegion {
    const mappable = pins.filter(hasMappableLocation);
    if (mappable.length === 0) {
        return {
            ...FALLBACK_CENTER,
            latitudeDelta: MIN_REGION_DELTA,
            longitudeDelta: MIN_REGION_DELTA
        };
    }

    const considered = pinsForOpeningRegion(mappable);
    const lats = considered.map((pin) => pin.lat);
    const lngs = considered.map((pin) => pin.lng);

    return {
        latitude: median(lats),
        longitude: median(lngs),
        latitudeDelta: clamp(
            (Math.max(...lats) - Math.min(...lats)) * OPENING_PADDING,
            MIN_REGION_DELTA,
            MAX_REGION_DELTA
        ),
        longitudeDelta: clamp(
            (Math.max(...lngs) - Math.min(...lngs)) * OPENING_PADDING,
            MIN_REGION_DELTA,
            MAX_REGION_DELTA
        )
    };
}

/**
 * The Google-Maps zoom level that shows `region` in a viewport of this size.
 *
 * The web map takes a zoom, not a region, and used to be hardcoded to 13 — so
 * even after the opening *centre* was fixed, web and native still disagreed
 * about how far out to start. `fitBounds` would be the obvious call, but it
 * silently does nothing when the map div has not been laid out yet, which is
 * exactly the moment the map is constructed. Computing the zoom up front works
 * whatever the div is doing.
 *
 * Standard Web Mercator: 256px tiles, `256 * 2^zoom` pixels spans 360° of
 * longitude, and latitude is measured in projected units rather than degrees.
 */
export function zoomForRegion(
    region: MapRegion,
    viewport: Viewport,
    /**
     * Google only accepts whole zoom levels, so the region always rounds one
     * way or the other by up to a factor of two.
     *
     * `contain` (default) rounds outwards: everything in the region stays on
     * screen, which is what opening the map wants. `cover` rounds inwards, for
     * when the *point* of the move is to get tighter than the region — flying
     * into a cluster to break it apart, where landing wider would leave it
     * merged and the tap would do nothing at all.
     */
    fit: "contain" | "cover" = "contain"
): number {
    const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    const width = viewport.width > 0 ? viewport.width : 390;
    const height = viewport.height > 0 ? viewport.height : 780;

    const zoomLng = Math.log2((width * 360) / (256 * region.longitudeDelta));
    const latSpan = Math.abs(
        mercatorY(region.latitude + region.latitudeDelta / 2) -
            mercatorY(region.latitude - region.latitudeDelta / 2)
    );
    const zoomLat = latSpan > 0 ? Math.log2((height * 2 * Math.PI) / (256 * latSpan)) : 21;
    const exact = Math.min(zoomLat, zoomLng);

    return clamp(fit === "cover" ? Math.ceil(exact) : Math.floor(exact), 3, 18);
}

/** The region to fly to when one pin is selected (~1.7 miles tall). */
export function selectedRegion(pin: { lat: number; lng: number }): MapRegion {
    return {
        latitude: pin.lat,
        longitude: pin.lng,
        latitudeDelta: 0.025,
        longitudeDelta: 0.025
    };
}

export interface Viewport {
    width: number;
    height: number;
}

export interface PinCluster<T extends ClusterablePin> {
    /** Stable across re-clusters: the id of the cluster's seed pin. Used as the
     *  React key, so a cluster that keeps its members keeps its marker view. */
    key: string;
    /** Centroid of the members — where the merged bubble is drawn. */
    lat: number;
    lng: number;
    members: T[];
}

/**
 * How much the map is willing to say about a pin at this scale.
 *
 * `overview` — she is looking at a whole region. Nothing about an individual
 * visit is legible at this scale and pretending otherwise is what produced the
 * pile, so everything within a bubble's reach merges regardless of colour and
 * the marker says one thing: how many. No pay, no relation colour, no dot.
 *
 * `grouped` — neighbourhood scale. Visits merge only with visits of their own
 * colour, so a green "yours" bubble never absorbs an amber "requested" one, and
 * the colour a bubble shows is always true of everything inside it. Zooming
 * further keeps splitting these until every visit stands alone.
 */
export type ClusterMode = "overview" | "grouped";

/**
 * Show more than this much longitude (~40 miles across at NYC latitude) and the
 * map switches to overview.
 *
 * **Longitude only, on purpose.** A phone is roughly twice as tall as it is
 * wide, so the same map shows about twice as many degrees of latitude as of
 * longitude; testing `latitudeDelta` against a single number made the threshold
 * a function of the handset rather than of how far out she is. It also has to
 * clear `MAX_REGION_DELTA` with room to spare, because the region the map
 * settles at is always wider than the one it was asked for — native expands to
 * the view's aspect ratio, and web can only take whole zoom levels, which
 * rounds outwards by up to a factor of two. Get this wrong and the map *opens*
 * in overview, counts-only, which is the one thing this mode must never do:
 * she gets colours by default and pinches out deliberately to trade them for
 * counts.
 */
export const OVERVIEW_LONGITUDE_DELTA = 0.6;

export function clusterModeForRegion(region: MapRegion): ClusterMode {
    return region.longitudeDelta > OVERVIEW_LONGITUDE_DELTA ? "overview" : "grouped";
}

/**
 * Bubble footprint in points. `nativeMarkerWrap` is `minWidth: 88` and about
 * 40pt tall including the arrow; two bubbles collide when their centres are
 * closer than that in both axes, which is exactly the test below. Deriving the
 * threshold from the real bubble size rather than a magic radius means the
 * clustering stays correct if the bubble is ever restyled.
 */
export const BUBBLE_WIDTH_PT = 88;
export const BUBBLE_HEIGHT_PT = 40;

/**
 * Project a coordinate into view points for a given region. A flat projection
 * is fine here: at these region sizes the Mercator error over the height of a
 * phone screen is well under a point, and the result is only ever used to ask
 * "would these two bubbles touch".
 */
function project(
    pin: { lat: number; lng: number },
    region: MapRegion,
    viewport: Viewport
): { x: number; y: number } {
    return {
        x:
            ((pin.lng - (region.longitude - region.longitudeDelta / 2)) / region.longitudeDelta) *
            viewport.width,
        y:
            ((region.latitude + region.latitudeDelta / 2 - pin.lat) / region.latitudeDelta) *
            viewport.height
    };
}

/**
 * Merge pins whose bubbles would overlap on screen at this region.
 *
 * Greedy, seeded from a deterministic order (north-to-south, then id) and
 * measured against the *seed* rather than a moving centroid — so the same
 * feed at the same region always produces the same clusters, and a background
 * refetch that changed nothing cannot reshuffle the map.
 *
 * `groupKey` is what keeps a merge honest: pass one and only pins that share a
 * key can end up in the same bubble. The screen passes the pin colour, so a
 * bubble's colour is always true of every visit inside it. Overview mode passes
 * nothing, because at that scale the marker has stopped claiming a colour.
 *
 * Two pins at the same address (every visit for one patient shares
 * `patients.lat/lng`) are exactly coincident and always land in one cluster,
 * at any zoom. That case is why splitting on zoom can't be the whole answer —
 * see `clusterSplitRegion`.
 */
export function clusterPins<T extends ClusterablePin>(
    pins: T[],
    region: MapRegion,
    viewport: Viewport,
    options?: { groupKey?: (pin: T) => string }
): PinCluster<T>[] {
    const mappable = pins.filter(hasMappableLocation);
    if (mappable.length === 0 || viewport.width <= 0 || viewport.height <= 0) {
        return [];
    }

    const groupKey = options?.groupKey;
    const ordered = [...mappable]
        .sort((a, b) => b.lat - a.lat || a.id - b.id)
        .map((pin) => ({ pin, ...project(pin, region, viewport) }));

    const claimed = new Set<number>();
    const clusters: PinCluster<T>[] = [];

    for (const seed of ordered) {
        if (claimed.has(seed.pin.id)) {
            continue;
        }
        claimed.add(seed.pin.id);

        const members: T[] = [seed.pin];
        const seedGroup = groupKey?.(seed.pin);

        for (const candidate of ordered) {
            if (claimed.has(candidate.pin.id)) {
                continue;
            }
            if (groupKey && groupKey(candidate.pin) !== seedGroup) {
                continue;
            }
            if (
                Math.abs(candidate.x - seed.x) < BUBBLE_WIDTH_PT &&
                Math.abs(candidate.y - seed.y) < BUBBLE_HEIGHT_PT
            ) {
                claimed.add(candidate.pin.id);
                members.push(candidate.pin);
            }
        }

        clusters.push({
            key: String(seed.pin.id),
            lat: members.reduce((sum, m) => sum + m.lat, 0) / members.length,
            lng: members.reduce((sum, m) => sum + m.lng, 0) / members.length,
            members
        });
    }

    return clusters;
}

/**
 * The tightest the map will drill in to separate a cluster (~0.3 miles tall,
 * about street level). Visits still stacked at this scale are at one address or
 * in one building, and no further zoom will ever prise them apart.
 */
export const MIN_SPLIT_DELTA = 0.004;

/**
 * Where to fly so that a tapped group of visits stops being one bubble, or
 * `null` when zooming can never separate them.
 *
 * This is the whole drill-down rule in one function: tapping a bubble zooms in
 * on what is inside it, again and again, until the visits stand alone — and the
 * list sheet only appears for the residue that zoom cannot fix. Testing it by
 * actually re-running the clustering at the candidate region (rather than
 * eyeballing a distance) means the answer stays right if the bubble is ever
 * resized.
 *
 * Deliberately grouped-blind: the question is only whether the *positions*
 * separate, so two visits of different colours sitting on the same doorstep
 * still resolve to the sheet rather than to a zoom that can't help.
 */
export function clusterSplitRegion<T extends ClusterablePin>(
    members: T[],
    viewport: Viewport
): MapRegion | null {
    if (members.length < 2) {
        return null;
    }

    const lats = members.map((m) => m.lat);
    const lngs = members.map((m) => m.lng);
    const candidate: MapRegion = {
        latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
        longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
        latitudeDelta: Math.max((Math.max(...lats) - Math.min(...lats)) * 1.6, MIN_SPLIT_DELTA),
        longitudeDelta: Math.max((Math.max(...lngs) - Math.min(...lngs)) * 1.6, MIN_SPLIT_DELTA)
    };

    return clusterPins(members, candidate, viewport).length > 1 ? candidate : null;
}

/**
 * Every cluster whose bubble physically touches `target`'s, `target` included.
 *
 * Clustering by colour means two bubbles of different colours can still land on
 * top of each other, and that is exactly the "I tapped the new one and got the
 * old one" case — so a tap resolves against everything under the finger, not
 * just the bubble that happened to receive the event.
 */
export function collidingClusters<T extends ClusterablePin>(
    clusters: PinCluster<T>[],
    target: PinCluster<T>,
    region: MapRegion,
    viewport: Viewport
): PinCluster<T>[] {
    const targetPoint = project(target, region, viewport);

    return clusters.filter((cluster) => {
        if (cluster.key === target.key) {
            return true;
        }
        const point = project(cluster, region, viewport);
        return (
            Math.abs(point.x - targetPoint.x) < BUBBLE_WIDTH_PT &&
            Math.abs(point.y - targetPoint.y) < BUBBLE_HEIGHT_PT
        );
    });
}

/**
 * How far apart two regions are, as a fraction of the current span. Used to
 * decide whether a pan/zoom is worth re-clustering for: MapKit fires
 * `onRegionChangeComplete` for sub-pixel settles too, and re-clustering on
 * every one of those would remount every marker under her finger.
 */
export function regionChangedEnough(a: MapRegion | null, b: MapRegion): boolean {
    if (!a) {
        return true;
    }
    const zoomed =
        Math.abs(Math.log2(a.latitudeDelta / b.latitudeDelta)) > 0.15 ||
        Math.abs(Math.log2(a.longitudeDelta / b.longitudeDelta)) > 0.15;
    const panned =
        Math.abs(a.latitude - b.latitude) > b.latitudeDelta * 0.08 ||
        Math.abs(a.longitude - b.longitude) > b.longitudeDelta * 0.08;
    return zoomed || panned;
}

// ═══════════════════════════════════════════════════
// Stacking order
// ═══════════════════════════════════════════════════

/** The bit of a visit that decides how high its bubble stacks. */
export interface StackablePin {
    relation: "open" | "pending" | "rejected" | "cancelled" | "assigned";
    /** null = she has never opened this visit's detail screen. */
    viewed: unknown | null;
}

/**
 * Which bubble wins when two still overlap after clustering.
 *
 * Every marker used to ship with no `zIndex` at all, which on iOS leaves every
 * annotation at zPosition/zPriority 0 and hands the stacking order to MapKit —
 * so a visit that had just been broadcast routinely drew *underneath* one she
 * had already turned down, and the tap went to whatever MapKit had put on top.
 * (`zIndex` is supported on iOS in react-native-maps 1.27.2:
 * `AIRMapMarker.getAnnotationView` sets both `layer.zPosition` and `zPriority`
 * from it. It was simply never passed.)
 *
 * The order is what she is trying to reach, most-wanted first:
 *   selected  — she has already tapped it; it must not hide behind a neighbour
 *   unseen    — a live offer she has never opened. This is the tap she is
 *               racing for when an agency releases a batch
 *   open      — an offer she has looked at
 *   pending   — already requested; nothing left to do but wait
 *   settled   — rejected/cancelled; informational
 *   assigned  — already hers, the least urgent thing on the map
 *
 * NOTE for anyone changing this at runtime: react-native-maps' iOS `setZIndex`
 * updates `layer.zPosition` but NOT `zPriority`, so a zIndex that changes after
 * the annotation view exists only restacks the drawing, not the hit-testing.
 * Keep a pin's zIndex stable for its lifetime and let selection be expressed by
 * the region animation and the ring instead.
 */
export function pinZIndex(pin: StackablePin, isSelected: boolean): number {
    if (isSelected) {
        return 1000;
    }
    if (pin.relation === "assigned") {
        return 100;
    }
    if (pin.viewed == null) {
        return 500;
    }
    switch (pin.relation) {
        case "open":
            return 400;
        case "pending":
            return 300;
        default:
            return 200;
    }
}

/** A merged bubble stacks as high as the most urgent thing inside it, so a
 *  cluster holding one brand-new offer is never buried under a cluster of
 *  visits she has already dealt with. */
export function clusterZIndex(members: StackablePin[], isSelected: boolean): number {
    return Math.max(...members.map((member) => pinZIndex(member, false)), isSelected ? 1000 : 0);
}
